import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, AlertCircle, Send, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminNotify = () => {
  const [parkName, setParkName] = useState('');
  const [siteNumber, setSiteNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingWatchers, setCheckingWatchers] = useState(false);
  const [result, setResult] = useState(null);
  const [watchersInfo, setWatchersInfo] = useState(null);
  const [error, setError] = useState(null);

  // Quick select options for common parks
  const quickParks = [
    'Algonquin - Mew Lake',
    'Algonquin - Canisbay',
    'Algonquin - Pog Lake',
    'Algonquin - Rock Lake',
    'Killarney',
    'Sandbanks',
    'Pinery',
    'Frontenac',
  ];

  const checkWatchers = async () => {
    if (!parkName || !siteNumber) {
      setError('Please enter both park name and site number');
      return;
    }

    setCheckingWatchers(true);
    setError(null);
    setWatchersInfo(null);

    try {
      const response = await axios.get(
        `${API_URL}/api/alerts/watching/${encodeURIComponent(parkName)}/${siteNumber}`
      );
      setWatchersInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check watchers');
    } finally {
      setCheckingWatchers(false);
    }
  };

  const markAvailable = async () => {
    if (!parkName || !siteNumber) {
      setError('Please enter both park name and site number');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/alerts/mark-available`, {
        park_name: parkName,
        site_number: parseInt(siteNumber),
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-green-800">🏕️ CampSight Admin</CardTitle>
            <CardDescription>
              Mark a campsite as available to notify all users watching it
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Park Selection */}
            <div>
              <Label className="text-sm font-medium text-gray-700">Quick Select Park</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {quickParks.map((park) => (
                  <Button
                    key={park}
                    variant={parkName === park ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setParkName(park)}
                    className={parkName === park ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    {park.split(' - ').pop()}
                  </Button>
                ))}
              </div>
            </div>

            {/* Park Name Input */}
            <div>
              <Label htmlFor="parkName">Park Name</Label>
              <Input
                id="parkName"
                placeholder="e.g., Algonquin - Mew Lake"
                value={parkName}
                onChange={(e) => setParkName(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Site Number Input */}
            <div>
              <Label htmlFor="siteNumber">Site Number</Label>
              <Input
                id="siteNumber"
                type="number"
                placeholder="e.g., 111"
                value={siteNumber}
                onChange={(e) => setSiteNumber(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={checkWatchers}
                disabled={checkingWatchers || !parkName || !siteNumber}
                variant="outline"
                className="flex-1"
              >
                <Users className="w-4 h-4 mr-2" />
                {checkingWatchers ? 'Checking...' : 'Check Watchers'}
              </Button>
              <Button
                onClick={markAvailable}
                disabled={loading || !parkName || !siteNumber}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Sending...' : 'Notify Users'}
              </Button>
            </div>

            {/* Watchers Info */}
            {watchersInfo && (
              <Alert className="bg-blue-50 border-blue-200">
                <Users className="h-4 w-4 text-blue-600" />
                <AlertDescription>
                  <strong>{watchersInfo.watchers_count} users</strong> are watching{' '}
                  {watchersInfo.park_name} Site #{watchersInfo.site_number}
                  {watchersInfo.alerts?.length > 0 && (
                    <ul className="mt-2 text-sm">
                      {watchersInfo.alerts.map((a, i) => (
                        <li key={i}>
                          • {a.email} ({a.start_date} to {a.end_date})
                        </li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Success Result */}
            {result && (
              <Alert className={result.notifications_sent > 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}>
                <CheckCircle className={`h-4 w-4 ${result.notifications_sent > 0 ? 'text-green-600' : 'text-yellow-600'}`} />
                <AlertDescription>
                  <strong>{result.message}</strong>
                  {result.notifications_sent > 0 && (
                    <div className="mt-2 text-sm">
                      Notified: {result.notified_emails?.join(', ')}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Error */}
            {error && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            {/* Instructions */}
            <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
              <strong>How to use:</strong>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>Spot an available site on Ontario Parks reservation website</li>
                <li>Enter the park name and site number here</li>
                <li>Click "Check Watchers" to see who will be notified</li>
                <li>Click "Notify Users" to send email alerts</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <a href="/" className="text-green-600 hover:text-green-800 text-sm">
            ← Back to CampSight
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminNotify;
